import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';

// Shared helper component to parse and render mixed text and math.
// Strategy: scan character by character. When a $ is found, look ahead for a
// closing $. Only treat the pair as LaTeX math if the content between them
// contains actual LaTeX indicators (backslash, ^, _, { or }).
// Plain currency like $45 or $105.75 has none of these, so it stays as plain text.
const MathParser = ({ text }) => {
  if (!text) return null;

  const result = [];
  let i = 0;
  let textBuffer = '';
  let keyCounter = 0;

  const flushText = () => {
    if (textBuffer) {
      result.push(<span key={`text-${keyCounter++}`}>{textBuffer}</span>);
      textBuffer = '';
    }
  };

  while (i < text.length) {
    // Not a dollar sign — accumulate as plain text
    if (text[i] !== '$') {
      textBuffer += text[i++];
      continue;
    }

    // Check for block math: $$...$$
    if (i + 1 < text.length && text[i + 1] === '$') {
      const start = i + 2;
      const closeIdx = text.indexOf('$$', start);
      if (closeIdx !== -1) {
        flushText();
        const content = text.substring(start, closeIdx);
        try {
          result.push(<BlockMath key={`math-${keyCounter++}`}>{content}</BlockMath>);
        } catch (e) {
          console.error('KaTeX block error:', content, e);
          result.push(<span key={`err-${keyCounter++}`} style={{ color: 'red' }}>{text.substring(i, closeIdx + 2)}</span>);
        }
        i = closeIdx + 2;
        continue;
      }
    }

    // Look for a closing $ to form an inline math pair
    let j = i + 1;
    while (j < text.length && text[j] !== '$') {
      if (text[j] === '\\') j++; // skip over escaped character
      j++;
    }

    if (j < text.length) {
      // Potential inline math pair found: text[i..j]
      const content = text.substring(i + 1, j);
      // Only treat as math if content has LaTeX syntax.
      // Currency like $45 or $105.75 has NO backslash/caret/braces, so it's skipped.
      const hasLatex = /[\\^_{}]/.test(content);
      if (hasLatex) {
        flushText();
        try {
          result.push(<InlineMath key={`math-${keyCounter++}`}>{content}</InlineMath>);
        } catch (e) {
          console.error('KaTeX inline error:', content, e);
          result.push(<span key={`err-${keyCounter++}`} style={{ color: 'red' }}>{`$${content}$`}</span>);
        }
        i = j + 1;
        continue;
      }
    }

    // $ is not part of a valid math pair — treat it as a plain text character
    textBuffer += text[i++];
  }

  flushText();

  return <>{result}</>;
};

export default MathParser;
